import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useClientV2} from "../../utils/hooks";
import {AxiosError} from "axios";

type MetaPixelStatusApiData = {
  isConnected?: boolean;
  metaPixelConnected?: boolean;
  connectedAt?: string | null;
  pixelId?: string | null;
  conversionsApiToken?: string | null;
};

type MetaPixelStatus = {
  pixelId: string | null;
  conversionsApiToken: string | null;
  metaPixelConnected: boolean;
  connectedAt: string | null;
};

function normalizeMetaPixelStatusPayload(data: unknown): MetaPixelStatus {
  const d = data as MetaPixelStatusApiData;

  return {
    pixelId: d?.pixelId ?? null,
    conversionsApiToken: d?.conversionsApiToken ?? null,
    metaPixelConnected: Boolean(d?.metaPixelConnected ?? d?.isConnected),
    connectedAt: d?.connectedAt ?? null,
  };
}

type ConnectMetaPixelPayload = {
  pixelId: string;
  conversionsApiToken: string;
};

export function useGetMetaPixelStatus() {
  const clientV2 = useClientV2();

  return useQuery<MetaPixelStatus, AxiosError<{status: boolean; message: string}>>({
    queryKey: ["meta_pixel_status"],
    queryFn: () =>
      clientV2("integrations/meta-pixel/status").then((res) =>
        normalizeMetaPixelStatusPayload((res as {data?: unknown}).data)
      ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useConnectMetaPixel() {
  const clientV2 = useClientV2();
  const queryClient = useQueryClient();

  return useMutation<{data: unknown}, AxiosError, ConnectMetaPixelPayload>({
    mutationFn: (payload) =>
      clientV2("integrations/meta-pixel/connect", {
        method: "POST",
        data: payload,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ["integrations_status"]});
      queryClient.invalidateQueries({queryKey: ["meta_pixel_status"]});
    },
  });
}

export function useDisconnectMetaPixel() {
  const clientV2 = useClientV2();
  const queryClient = useQueryClient();

  return useMutation<{data: unknown}, AxiosError, void>({
    mutationFn: () =>
      clientV2("integrations/meta-pixel/disconnect", {
        method: "DELETE",
      }),
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ["integrations_status"]});
      queryClient.invalidateQueries({queryKey: ["meta_pixel_status"]});
    },
  });
}
